import { BlogListPage, ChatBot, NextPageHead, PageView, BlogListPageVariant } from '@aspendental/shared-components-web';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import convertPathToBreadCrumbTitle from '../utils/convert-path-to-breadcrumb-title/convert-path-to-breadcrumb-title';
import DetermineIndexing from '../utils/determine-indexing';
import getHostFromHeaders from '../utils/get-host-from-headers';
import { CACHE_CONTROL_NO_CACHE, CACHE_CONTROL_SHORT_TTL } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, BlogListPageByBrandDocument, BlogListPageFragment, PageFragment } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import choosePreview from '@src/pages/utils/choose-preview';
import getConfigByBrand from '@src/pages/utils/get-config-by-brand';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getDeviceTypeFromHeaders } from '@src/pages/utils/get-device-type-from-headers';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import getThemeByDomainName from '@src/pages/utils/get-theme-by-domain-name';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    siteURL,
    features,
    featureFlags,
    logo
  } = props.config as BrandThemeConfig;
  const {
    url: logoUrl
  } = props.appData?.logo ?? {};
  const {
    pageData,
    preview,
    breadcrumbsData,
    pageReferrer,
    domain
  } = props;
  const path = features.blogs.root;
  const {
    metaTitle,
    metaDescription,
    canonical,
    indexed,
    analyticsContext,
    ogImage
  } = pageData;
  const BlogComponent = (pageData.variant ?? 'default') === 'default' ? BlogListPage : BlogListPageVariant;
  return <>
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Marketing" data-sentry-element="PageView" data-sentry-source-file="index.page.tsx" />
			<NextPageHead path={path} title={metaTitle} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={metaDescription} canonical={canonical} originUrl={siteURL} breadcrumbTitle={breadcrumbsData.breadcrumbTitle} breadcrumbs={breadcrumbsData.breadcrumbsCollection.items} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="index.page.tsx" />
			<BlogComponent {...pageData} preview={preview} data-sentry-element="BlogComponent" data-sentry-source-file="index.page.tsx" />
			{features.chat.enabled && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  req,
  res,
  preview = false
}) => {
  const domain = getHostFromHeaders(req?.headers);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const theme = getThemeByDomainName(domain);
  const headerTagTheme = Array.isArray(req.headers['x-tag-theme']) ? (req.headers['x-tag-theme'] as TagThemeType[])[0] : req.headers['x-tag-theme'] as TagThemeType;
  // Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
  const tagTheme = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
  const logger = getLogger('blog-list-page', tagTheme);
  const pageReferrer = req.headers.referer || '';
  try {
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(domain, preview);
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const gqlClient = getClient(preview);
    if (gqlClient === null) {
      logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const [appPagesData, appMetadata] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise()]);
    const appData = {
      ...appPagesData.data?.appCollection?.items[0],
      ...appMetadata.data?.appCollection?.items[0]
    };

    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      return {
        notFound: true
      };
    }
    const pageData = await gqlClient.query(BlogListPageByBrandDocument, {
      brandName: brandConfig.name,
      preview
    }).toPromise();
    const page = pageData.data?.blogListPageCollection?.items[0] as BlogListPageFragment;
    if (pageData.error) {
      logger.error(`CONTENTFUL_ERROR_BLOG_LIST_PAGE: ${pageData.error}`);
    }
    if (!page) {
      logger.error(`COULD_NOT_FIND_THE_BLOG_HOME_PAGE`);
      return {
        notFound: true
      };
    }

    // breadcrumb data generation for blog list page
    const {
      features: {
        blogs: {
          root
        }
      }
    } = brandConfig;
    const breadcrumbsData = {
      breadcrumbTitle: convertPathToBreadCrumbTitle(root),
      path: root,
      breadcrumbsCollection: {
        items: [{
          breadcrumbTitle: 'Home',
          path: '/'
        } as PageFragment]
      }
    };
    if (preview) {
      res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
    } else {
      res.setHeader('Cache-Control', CACHE_CONTROL_SHORT_TTL);
    }
    let hasIpBasedCoordinates = false;
    let coordinates = {
      latitude: 0,
      longitude: 0
    };
    if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
      hasIpBasedCoordinates = true;
      coordinates = {
        latitude: parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
        longitude: parseFloat(req.headers['cloudfront-viewer-longitude'] as string)
      };
      logger.info(`CDN_GEO_LOCATION_FOUND: [${coordinates?.latitude}, ${coordinates?.longitude}]`);
    } else {
      logger.info(`CDN_GEO_LOCATION_MISS`);
    }
    const transformedServices = getConfigServices(brandConfig);
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        preview,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
          CC_DENTRINO_CLIENT_ID: process.env.CC_DENTRINO_CLIENT_ID ?? '',
          CC_DENTRINO_CLIENT_SECRET: process.env.CC_DENTRINO_CLIENT_SECRET ?? '',
          DENTRINO_CLIENT_ID: process.env.DENTRINO_CLIENT_ID,
          DENTRINO_CLIENT_SECRET: process.env.DENTRINO_CLIENT_SECRET
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        pageData: page,
        user: {
          coordinates,
          hasIpBasedCoordinates
        },
        appData,
        footerData: appData?.footer,
        breadcrumbsData,
        deviceType: deviceType,
        pageReferrer,
        domain: domain ?? ''
      }
    };
  } catch (e) {
    logger.error(`BLOG_LIST_PAGE_LOAD_FAILURE: ${e}`);
    return {
      notFound: true
    };
  }
};
export default Page;